//@ts-check
import {downloadCsvFromCdnUrl, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {useDataTableV2} from '@/hooks/useDataTableV2'
import {getProductCsvFile, getProductList} from '@/services/ProductsService/product'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import gb from '@/utils/growthbookUtils'
import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {useCallback, useMemo, useState} from 'react'

/** @typedef {{column: string, direction: 'asc' | 'desc'}} SortEntry */

/** @param {SortEntry | null} sorting @param {number} pageIndex @param {number} pageSize */
const buildQueryParams = (sorting, pageIndex, pageSize) => {
  const params = new URLSearchParams()
  params.append('sortBy', sorting?.column ?? 'productId')
  // Uppercased so the seeded default sort (`desc`) produces the same query key as the unsorted fallback.
  params.append('order', (sorting?.direction ?? 'DESC').toUpperCase())
  params.append('page', String(pageIndex))
  params.append('pageSize', String(pageSize))
  return params.toString()
}

/**
 * Data source hook for the v2 Product List table — wires `useDataTableV2` state
 * (pagination/sorting/filters) into the `getProductList` query, scoped to non-combo products.
 * @param {number} [defaultPageSize]
 * @param {string | null} [businessGroup] When set, adds a businessGroup filter to every request.
 * @param {string[]} [visibleColumnKeys] Column keys to include in CSV downloads.
 * @param {object[]} [columns] Column definitions
 * @param {boolean} [isCombo] When true, fetches combo products instead of standard products.
 */
export const useProductListTableV2 = (
  defaultPageSize = DEFAULT_TABLE_PAGE_SIZE,
  businessGroup = null,
  visibleColumnKeys = [],
  columns = [],
  isCombo = false
) => {
  const [isComparing, setIsComparing] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const debounceMS = gb.getFeatureValue(growthBookFeatureFlags.ProductSearchDebounceDelay, 2000)

  const {
    pageIndex,
    pageSize,
    sorting,
    activeFilters,
    selectedRows,
    isAllRowsSelected,
    resetKey,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    handleResetAll,
    onTableReady,
    rowSelectionStateUpdater,
    initialRowSelection,
    clearSelection,
    tableRef,
  } = useDataTableV2({
    defaultPageSize,
    debounceMs: debounceMS,
    columns,
    defaultSorting: {column: 'productId', direction: 'desc'},
  })

  const singleSearchColumnKeys = useMemo(
    () => new Set(columns.filter(col => col.multiSearch === false).map(col => col.columnKey)),
    [columns]
  )

  const searchFilters = useMemo(
    () => [
      ...activeFilters.map(filter =>
        filter.values && singleSearchColumnKeys.has(filter.field)
          ? {field: filter.field, value: filter.values[0], type: filter.type}
          : filter
      ),
      {field: 'isCombo', value: String(isCombo), type: 'string'},
      ...(businessGroup ? [{field: 'businessGroup', value: businessGroup, type: 'string'}] : []),
    ],
    [activeFilters, businessGroup, isCombo, singleSearchColumnKeys]
  )
  const filtersKey = useMemo(() => JSON.stringify(searchFilters), [searchFilters])
  const queryParams = buildQueryParams(sorting, pageIndex, pageSize)

  const {data, isFetching} = useQuery({
    queryKey: [isCombo ? 'COMBO_PRODUCT_LIST' : 'PRODUCT_LIST_V2', queryParams, filtersKey, searchFilters],
    queryFn: () => getProductList(searchFilters, queryParams),
    placeholderData: keepPreviousData,
  })

  const handleResetAllClick = useCallback(() => {
    handleResetAll()
    setIsComparing(false)
  }, [handleResetAll])

  const resetFilterButtonOptions = useMemo(
    () => ({onFiltersReset: handleResetAllClick, text: 'Reset All'}),
    [handleResetAllClick]
  )

  const canCompare = selectedRows.length >= 2 && !isAllRowsSelected
  const toggleCompareMode = useCallback(() => setIsComparing(prev => !prev), [])

  const canDownload = selectedRows.length > 0 || isAllRowsSelected

  const onDownloadClick = useCallback(async () => {
    if (isDownloading || !canDownload) return
    setIsDownloading(true)
    try {
      const ids = selectedRows.map(row => row.productId)
      const data = await getProductCsvFile({ids, filters: searchFilters, columns: visibleColumnKeys}, queryParams)
      downloadCsvFromCdnUrl(data.cdnUrl, `ProductList_${getDateWithTimeString()}`)
    } finally {
      setIsDownloading(false)
    }
  }, [isDownloading, canDownload, isAllRowsSelected, selectedRows, searchFilters, queryParams, visibleColumnKeys])

  // DataTable resolves row identity from an `id` field, falling back to row index -
  // add a stable one so selection survives switching between the full list and compare view.
  const baseTableData = useMemo(
    () =>
      (data?.data ?? []).map(product => ({
        ...product,
        id: product.productId,
        ...(product.multipleSellingUnits != null && {
          multipleSellingUnits: String(product.multipleSellingUnits).replace(/x/gi, ''),
        }),
      })),
    [data]
  )

  const tableData = isComparing ? selectedRows : baseTableData

  const selectionResetKey = isComparing ? null : `${sorting?.column}:${sorting?.direction}:${filtersKey}`

  return {
    tableData,
    isFetching,
    pageIndex: isComparing ? 0 : isFetching ? pageIndex : (data?.currentPage ?? pageIndex),
    pageSize: data?.pageSize ?? pageSize,
    totalRowsCount: isComparing ? tableData.length : (data?.totalRecords ?? 0),
    manualPagination: !isComparing,
    manualSorting: !isComparing,
    manualFiltering: !isComparing,
    selectedRows,
    isAllRowsSelected,
    resetKey,
    selectionResetKey,
    isComparing,
    canCompare,
    toggleCompareMode,
    canDownload,
    isDownloading,
    onDownloadClick,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    resetFilterButtonOptions,
    onTableReady,
    rowSelectionStateUpdater,
    initialRowSelection,
    resetAll: handleResetAllClick,
    clearSelection,
    tableRef,
  }
}
